import { Trash2, Tag as TagIcon, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

interface BatchActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onDelete: () => void;
  onAddTag: (tagId: string) => void;
  onSetPrivacy: (isPublic: boolean) => void;
  availableTags: Array<{ id: string; name: string }>;
}

export function BatchActionsBar({
  selectedCount,
  onClearSelection,
  onDelete,
  onAddTag,
  onSetPrivacy,
  availableTags,
}: BatchActionsBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5">
      <div className="bg-card border shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
        <Badge variant="secondary" className="px-3 py-1">
          {selectedCount} selected
        </Badge>

        <div className="h-6 w-px bg-border" />

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <TagIcon className="h-4 w-4 mr-2" />
                Add Tag
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="max-h-[300px] overflow-y-auto">
              <DropdownMenuLabel>Add Tag to Selected</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableTags.length > 0 ? (
                availableTags.map((tag) => (
                  <DropdownMenuItem key={tag.id} onClick={() => onAddTag(tag.id)}>
                    {tag.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No tags available
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                Privacy
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuLabel>Set Privacy</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onSetPrivacy(true)}>
                <Eye className="h-4 w-4 mr-2" />
                Make Public
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSetPrivacy(false)}>
                <EyeOff className="h-4 w-4 mr-2" />
                Make Private
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" variant="destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>

          <div className="h-6 w-px bg-border" />

          <Button
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
